import {
  Box,
  Button,
  ButtonGroup,
  RadioGroup as ChakraRadioGroup,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Radio,
  Stack,
  Switch,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import type {
  Addon,
  InsuranceParamsDto,
  InsurerDiscount
} from 'BrokerTsLib/src/dtos'
import {
  ToAddon,
} from 'BrokerTsLib/src/dtos'
import { Insurer, Risk, RiskVariant, Selection } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { usePriceFormat } from '@/hooks/usePriceFormat'
import type { Ref } from 'react'
import { useEffect, useRef, useState } from 'react'

import { CustomSlider } from '@/components/CustomSlider'
import { SliderLabel } from '@/components/ui/SliderLabel'
import CompaniesSelection from '@/containers/forms/CompaniesSelection'
import { CompanyCheckbox } from '@/containers/forms/company-checkbox'
import { CompanyCheckboxGroup } from '@/containers/forms/CompanyCheckboxGroup'
import { FormRow } from '@/containers/forms/FormRow'
import { useAuth } from '@/context/AuthContext'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePartner } from '@/context/PartnerContext'
import { useInsurers, useRiskData } from '@/hooks'
import { useGetInsuranceParamsDiscounts } from '@/hooks/useInsuranceParams'
import { useCebiGlass } from '@/hooks/useVehicle'
import { getLogo } from '@/utils/companiesLogos'

import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import VariantSelectionCheckbox from './VariantSelectionCheckbox'
import VariantSelectionRadio from './VariantSelectionRadio'

const GlassSliderState = {
  ShowEvaluate: 0,
  ShowValuations: 1,
  ShowValue: 2,
}
export type GlassSliderState = typeof GlassSliderState[keyof typeof GlassSliderState]

export const ParametersForm = ({ onDiscountsLoaded }: { onDiscountsLoaded: (discounts: InsurerDiscount[]) => void }) => {
  const { app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas

  const {
    insuranceParamsId,
    insuranceParams: ip,
    isDisabled,
    isSubaruInsurance
  } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsDto
  const {
    setValue,
    getValues,
    watch,
    formState: { isSubmitSuccessful, isSubmitting },

  } = useFormContext<InsuranceParamsDto>()
  const { onToggle, isOpen } = useDisclosure()
  const { user } = useAuth()
  const { currentPartner } = usePartner()
  const { priceFormat } = usePriceFormat()
  const cebiGlass = !!user?.cebiGlass

  const isJap = currentPartner?.partner === 'Jap'

  const refAddons = useRef<HTMLDivElement>()
  if (refAddons.current) {
    refAddons.current.style.overflow = 'visible'
  }

  const { data: allInsurersData } = useInsurers(false, false)
  const { data: allAddonInsurersData } = useInsurers(true, false)

  const allInsurers = (allInsurersData?.data ?? []).filter((x) =>
    isSubaruInsurance ? x.id !== Insurer.Kooperativa : true,
  )
  const allAddonInsurers = allAddonInsurersData?.data ?? []

  const selectedAddonInsurers = watch('baseParams.addonInsurers') ?? []
  const isSelectedAllAddonInsurers =
    selectedAddonInsurers.length === allAddonInsurers?.length

  const [insurersDiscounts, setInsurersDiscounts] = useState<InsurerDiscount[] | undefined>(undefined)
  const { refetch: refetchInsurersDiscounts } = useGetInsuranceParamsDiscounts(
    insuranceParamsId!,
    {
      enabled: !isPublicPoleas,
      refetchOnWindowFocus: false,
    },
    (data) => {
      if (data) {
        setInsurersDiscounts(data)
        setValue(
          'baseParams.insurerDiscounts',
          data ?? [],
        )
      }
      onDiscountsLoaded(data ?? [])
    }
  )

  useEffect(() => {
    // reset ve formulari, pokud se zmeni pocatek z headeru
    setValue('generalParams.insuranceStartDate', insuranceParams?.generalParams.insuranceStartDate)
  }, [insuranceParams?.generalParams.insuranceStartDate])

  useEffect(() => {
    if (!isSubmitting && isSubmitSuccessful && !isPublicPoleas) refetchInsurersDiscounts() // slevy se muzou na serveru zmenit, tak je radeji prenactu at mam aktualni hodnoty
  }, [isSubmitting, isSubmitSuccessful, isPublicPoleas, refetchInsurersDiscounts])

  const {
    mutateAsync: getCebiGlass,
    data: cebiGlasssData,
    isPending: isLoadingCebiGlass,
  } = useCebiGlass()

  const addons = watch('addons') ?? []

  const {
    selected: selectedAssistance,
    value: assistance,
    sliderDS: assistanceSliderDS,
    min: assistanceMin,
    max: assistanceMax,
  } = useRiskData(Risk.ASISTENCE, addons)
  const {
    selected: selectedGlass,
    value: glass,
    sliderDS: glassSliderDS,
    min: glassMin,
    max: glassMax,
  } = useRiskData(Risk.SKLA, addons)
  const {
    selected: selectedNahr,
    value: nahr,
    sliderDS: nahrSliderDS,
    min: nahrMin,
    max: nahrMax,
  } = useRiskData(Risk.NAHR, addons)
  const {
    selected: selectedSeats,
    value: seats,
    sliderDS: seatsSliderDS,
    min: seatsMin,
    max: seatsMax,
  } = useRiskData(Risk.SEDADLA, addons)
  const {
    selected: selectedZivel,
    value: zivel,
    sliderDS: zivelSliderDS,
    min: zivelMin,
    max: zivelMax,
  } = useRiskData(Risk.ZIVEL, addons)
  const {
    selected: selectedZver,
    value: zver,
    sliderDS: zverSliderDS,
    min: zverMin,
    max: zverMax,
  } = useRiskData(Risk.ZVER, addons)
  const { value: directLiquidation } = useRiskData(
    Risk.DirectLiquidation,
    addons,
  )
  const { value: odcizeni } = useRiskData(Risk.ODCPOV, addons)

  const [glassSliderState, setGlassSliderState] = useState<GlassSliderState>(
    GlassSliderState.ShowEvaluate,
  )
  const [glassChanged, setGlassChanged] = useState(false)
  const [glassSliderValue, setGlassSliderValue] = useState<number | undefined>()

  // init na pageload -
  useEffect(() => {
    const sliderValue =
      (selectedGlass.riskVariantId ?? RiskVariant.WithoutGlassInsurance) ==
        RiskVariant.WithoutGlassInsurance && !glassChanged
        ? undefined // na init se zobrazuji button
        : glassSliderDS.find((x) => x.id == selectedGlass.riskVariantId)?.i

    setGlassSliderValue(sliderValue)

    // pokud je nejaka hodnota slideru, tak ji rovnou zobrazim (nezobrazuji buttony na oceneneni)
    if (sliderValue && !glassChanged) {
      setGlassSliderState(GlassSliderState.ShowValue)
    }
  }, [glassChanged, selectedGlass.riskVariantId, glassSliderDS.length])

  const setAddon = (addon: Addon) => {
    const addons = (getValues('addons') ?? []).filter(
      (x) => x.riskId !== addon.riskId,
    )

    addons.push(addon)

    setValue('addons', addons)
  }

  const handleEvaluateWindows = () => {
    getCebiGlass({
      licencePlate: getValues('vehicle.licensePlate'),
      vin: getValues('vehicle.vin'),
    }).then(({ data }) => {
      if (
        (data.PriceOriginal ?? 0) > 0 &&
        (data.PriceSecondaryManufacturing ?? 0) > 0
      )
        setGlassSliderState(GlassSliderState.ShowValuations)
      else if ((data.PriceOriginal ?? 0) > 0) {
        selectGlassValue(data.PriceOriginal!)
      } else if ((data.PriceSecondaryManufacturing ?? 0) > 0) {
        selectGlassValue(data.PriceSecondaryManufacturing!)
      }
    })
  }

  const selectGlassValue = (value: number) => {
    const values =
      glassSliderDS
        .filter((x) => !!x.value)
        .map((x) => ({ value: parseInt(x.value!), id: x.id })) ?? []
    if (values.length == 0) return

    // prvni stejny nebo vetsi
    let selected
    let filtered =
      values
        .filter((x) => x.value >= value)
        .sort((a, b) => a.value - b.value) ?? []
    if (filtered.length > 0) selected = filtered[0].id

    // prvni mensi
    if (!selected) {
      filtered =
        values
          .filter((x) => x.value < value)
          .sort((a, b) => b.value - a.value) ?? []
      if (filtered.length > 0) selected = filtered[0].id
    }

    if (selected) {
      setGlassChanged(true)
      setAddon(ToAddon(selected.toString() ?? '', Risk.SKLA))
      setGlassSliderState(GlassSliderState.ShowValue)
    }
  }

  const handleSelectWindows = () => {
    setGlassSliderState(GlassSliderState.ShowValue)
  }

  useEffect(() => {
    if (allInsurers.length > 0 && getValues('baseParams.insurers')?.length == 0)
      setValue(
        'baseParams.insurers',
        allInsurers.map(({ id }) => id),
      )
  }, [allInsurers.length])

  useEffect(() => {
    if (
      allAddonInsurers.length > 0 &&
      getValues('baseParams.addonInsurers')?.length == 0
    )
      setValue(
        'baseParams.addonInsurers',
        allAddonInsurers.map(({ id }) => id),
      )
  }, [allAddonInsurers.length])

  let glassMinLabel: string | React.ReactNode = ''
  if (glassSliderState == GlassSliderState.ShowValuations)
    glassMinLabel = <Text fontWeight="bold">Nové</Text>
  else if (
    glassSliderState == GlassSliderState.ShowValue &&
    glassSliderDS &&
    glassMin
  )
    glassMinLabel = priceFormat(
      parseInt(glassMin.value!),
      glassMin.name,
    )

  let glassMaxLabel: string | React.ReactNode = ''
  if (glassSliderState == GlassSliderState.ShowValuations)
    glassMaxLabel = <Text fontWeight="bold">Aftermarket</Text>
  else if (
    glassSliderState == GlassSliderState.ShowValue &&
    glassSliderDS &&
    glassMax
  )
    glassMaxLabel = priceFormat(parseInt(glassMax.value!))

  return (
    <>
      {user?.allowMultipleVariantSelection ? (
        <VariantSelectionCheckbox />
      ) : (
        <VariantSelectionRadio isDisabled={isDisabled} />
      )}
      {isJap && (
        <VStack align="start">
          <Box sx={{ fontWeight: 'bold' }}>Doplňková pojištění</Box>
          <Box>
            {!isPublicPoleas && 'Nastavení nemusí být u všech pojišťoven dostupné, zkontrolujte nabídky před předáním klientovi.'}
            {isPublicPoleas && 'Nastavení nemusí být u všech pojišťoven dostupné, zkontrolujte proto úplnost jednotlivých nabídek.'}
          </Box>
        </VStack>
      )}

      <FormRow>
        {assistanceSliderDS && assistanceMin && assistanceMax && (
          <FormControl sx={{ gap: 4 }}>
            <SliderLabel
              label="Asistence"
              value={assistance?.name ?? 'Bez asistence'}
            />

            <CustomSlider
              value={
                assistanceSliderDS.find(
                  (x) => x.id == selectedAssistance?.riskVariantId,
                )?.i ?? 0
              }
              labels={[
                { label: assistanceMin?.name ?? '', value: assistanceMin?.i },
                { label: assistanceMax?.name ?? '', value: assistanceMax?.i },
              ]}
              min={0}
              max={assistanceSliderDS.length - 1}
              step={1}
              onChange={(val) => {
                setAddon(
                  ToAddon(
                    assistanceSliderDS.find((x) => x.i == val)?.id.toString() ??
                    '',
                    Risk.ASISTENCE,
                  ),
                )
              }}
              isDisabled={isDisabled}
            />
          </FormControl>
        )}

        {
          glassSliderDS &&
          glassMin &&
          glassMax &&
          insuranceParams.vehicle.druh != Selection.Motorcycle && (
            <FormControl sx={{ gap: 4, position: 'relative' }}>
              <SliderLabel
                label="Skla"
                value={
                  glass?.value &&
                  priceFormat(parseInt(glass.value!), glass.name)
                }
                additionalButton={
                  cebiGlass && glassSliderState == GlassSliderState.ShowValue ? (
                    <Button
                      variant="link"
                      colorScheme="primary"
                      isLoading={isLoadingCebiGlass}
                      onClick={handleEvaluateWindows}
                      isDisabled={isDisabled}
                    >
                      Ocenit
                    </Button>
                  ) : undefined
                }
              />

              <CustomSlider
                value={glassSliderValue}
                labels={[
                  {
                    label: glassMinLabel,
                    value: parseInt(glassMin.value!),
                  },
                  {
                    label: glassMaxLabel,
                    value: parseInt(glassMax.value!),
                  },
                ]}
                min={0}
                max={glassSliderDS.length - 1}
                step={1}
                onChange={(val) => {
                  setAddon(
                    ToAddon(
                      glassSliderDS.find((x) => x.i == val)?.id.toString() ??
                      '',
                      Risk.SKLA,
                    ),
                  )
                }}
                isDisabled={isDisabled}
              />

              {glassSliderState == GlassSliderState.ShowEvaluate && (
                <Grid
                  sx={{
                    position: 'absolute',
                    mt: 1.5,
                    inset: 0,
                    w: '100%',
                    h: '100%',
                    zIndex: 1,
                  }}
                >
                  <ButtonGroup isAttached sx={{ bgColor: 'white', h: 7, mx: 'auto', mt: 6 }}>
                    <Button
                      type="button"
                      width="120px"
                      colorScheme="green"
                      isLoading={isLoadingCebiGlass}
                      onClick={handleEvaluateWindows}
                      isDisabled={isDisabled}
                    >
                      Ocenit
                    </Button>
                    <Button
                      type="button"
                      width="120px"
                      colorScheme="primary"
                      isLoading={isLoadingCebiGlass}
                      onClick={handleSelectWindows}
                      isDisabled={isDisabled}
                    >
                      Vybrat
                    </Button>
                  </ButtonGroup>
                </Grid>
              )}

              {glassSliderState == GlassSliderState.ShowValuations && (
                <Grid
                  sx={{
                    position: 'absolute',
                    mt: 1.5,
                    inset: 0,
                    w: '100%',
                    h: '100%',
                    zIndex: 1,
                  }}
                >
                  <ButtonGroup isAttached sx={{ bgColor: 'white', h: 7, mx: 'auto', mt: 6 }}>
                    {cebiGlasssData!.data.PriceOriginal && cebiGlasssData!.data.PriceOriginal > 0 && (
                      <Button
                        type="button"
                        width="120px"
                        colorScheme="green"
                        onClick={() =>
                          selectGlassValue(
                            cebiGlasssData!.data.PriceOriginal!,
                          )
                        }
                        isDisabled={isDisabled}
                      >
                        {priceFormat(cebiGlasssData!.data.PriceOriginal)}
                      </Button>
                    )}

                    {cebiGlasssData!.data.PriceSecondaryManufacturing && cebiGlasssData!.data.PriceSecondaryManufacturing > 0 && (
                      <Button
                        type="button"
                        width="120px"
                        colorScheme="primary"
                        onClick={() =>
                          selectGlassValue(
                            cebiGlasssData!.data.PriceSecondaryManufacturing!,
                          )
                        }
                        isDisabled={isDisabled}
                      >
                        {priceFormat(
                          cebiGlasssData!.data.PriceSecondaryManufacturing,
                        )}
                      </Button>
                    )}
                  </ButtonGroup>
                </Grid>
              )}

              {cebiGlasssData?.data.error && (
                <Text variant="danger" fontSize="small">
                  {cebiGlasssData?.data.error}
                </Text>
              )}
            </FormControl>
          )}
      </FormRow>

      {user?.collapsableBaseAddons && (
        <Flex justifyContent="center">
          <Button
            size="sm"
            onClick={onToggle}
            variant="link"
            isDisabled={isDisabled}
          >
            {!isOpen && 'Zobrazit další připojištění'}
            {isOpen && 'Skrýt další připojištění'}
          </Button>
        </Flex>
      )}

      <Collapse
        in={isOpen || !user?.collapsableBaseAddons}
        ref={refAddons as Ref<HTMLDivElement>}
      >
        <FormRow>
          {nahrSliderDS && nahrMin && nahrMax && (
            <FormControl sx={{ gap: 4 }}>
              <SliderLabel label="Náhradní vozidlo" value={nahr?.name} />

              <CustomSlider
                value={
                  nahrSliderDS.find((x) => x.id == selectedNahr?.riskVariantId)
                    ?.i ?? 0
                }
                labels={[
                  { label: nahrMin.name, value: parseInt(nahrMin.value!) },
                  { label: nahrMax.name, value: parseInt(nahrMax.value!) },
                ]}
                min={0}
                max={nahrSliderDS.length - 1}
                step={1}
                onChange={(val: number) => {
                  setAddon(
                    ToAddon(
                      nahrSliderDS.find((x) => x.i == val)?.id.toString() ?? '',
                      Risk.NAHR,
                    ),
                  )
                }}
                isDisabled={isDisabled}
              />
            </FormControl>
          )}

          {seatsSliderDS && seatsMin && seatsMax && (
            <FormControl sx={{ gap: 4 }}>
              <SliderLabel label="Úraz osob" value={seats?.name} />

              <CustomSlider
                value={
                  seatsSliderDS.find(
                    (x) => x.id == selectedSeats?.riskVariantId,
                  )?.i ?? 0
                }
                labels={[
                  { label: seatsMin.name, value: parseInt(seatsMin.value!) },
                  { label: seatsMax.name, value: parseInt(seatsMax.value!) },
                ]}
                min={0}
                max={seatsSliderDS.length - 1}
                step={1}
                onChange={(val: number) => {
                  setAddon(
                    ToAddon(
                      seatsSliderDS.find((x) => x.i == val)?.id.toString() ??
                      '',
                      Risk.SEDADLA,
                    ),
                  )
                }}
                isDisabled={isDisabled}
              />
            </FormControl>
          )}

          {zivelSliderDS && zivelMin && zivelMax && (
            <FormControl sx={{ gap: 4 }}>
              <SliderLabel label="Živel" value={zivel?.name} />

              <CustomSlider
                value={
                  zivelSliderDS.find(
                    (x) => x.id == selectedZivel?.riskVariantId,
                  )?.i ?? 0
                }
                labels={[
                  { label: zivelMin.name, value: parseInt(zivelMin.value!) },
                  { label: zivelMax.name, value: parseInt(zivelMax.value!) },
                ]}
                min={0}
                max={zivelSliderDS.length - 1}
                step={1}
                onChange={(val: number) => {
                  setAddon(
                    ToAddon(
                      zivelSliderDS.find((x) => x.i == val)?.id.toString() ??
                      '',
                      Risk.ZIVEL,
                    ),
                  )
                }}
                isDisabled={isDisabled}
              />
            </FormControl>
          )}

          {/* directLiquidation */}
          <FormControl sx={{ gap: 4 }}>
            <SliderLabel
              label="Přímá likvidace"
              value={
                directLiquidation?.id == RiskVariant.DirectLiquidationYes
                  ? 'Ano'
                  : 'Ne'
              }
            />

            <Switch
              isChecked={
                directLiquidation?.id == RiskVariant.DirectLiquidationYes
              }
              size="lg"
              onChange={(e) => {
                setAddon({
                  riskId: Risk.DirectLiquidation,
                  riskVariantId: e.target.checked
                    ? RiskVariant.DirectLiquidationYes
                    : RiskVariant.DirectLiquidationNo,
                })
              }}
              isDisabled={isDisabled}
            />
          </FormControl>

          {zverSliderDS && zverMin && zverMax && (
            <FormControl sx={{ gap: 4 }}>
              <SliderLabel label="Střet se zvěří" value={zver?.name} />

              <CustomSlider
                value={
                  zverSliderDS.find((x) => x.id == selectedZver?.riskVariantId)
                    ?.i ?? 0
                }
                labels={[
                  { label: zverMin.name, value: parseInt(zverMin.value!) },
                  { label: zverMax.name, value: parseInt(zverMax.value!) },
                ]}
                min={0}
                max={zverSliderDS.length - 1}
                step={1}
                onChange={(val: number) => {
                  setAddon(
                    ToAddon(
                      zverSliderDS.find((x) => x.i == val)?.id.toString() ?? '',
                      Risk.ZVER,
                    ),
                  )
                }}
                isDisabled={isDisabled}
              />
            </FormControl>
          )}

          {/* <FormControl sx={{ gap: 4 }}>
            <SliderLabel
              label="Střet se zvěří"
              value={
                zver?.id == RiskVariant.ZverYes
                  ? 'Ano'
                  : 'Ne'
              }
            />

            <Switch
              isChecked={zver?.id == RiskVariant.ZverYes}
              size="lg"
              dir="rtl"
              onChange={e => {
                setAddon({ riskId: Risk.ZVER, riskVariantId: e.target.checked ? RiskVariant.ZverYes : RiskVariant.ZverNo })
              }}
            />
          </FormControl> */}

          <FormControl sx={{ gap: 4 }}>
            <SliderLabel
              label="Odcizení celého vozidla"
              value={odcizeni?.id == RiskVariant.OdcizeniYes ? 'Ano' : 'Ne'}
            />

            <Switch
              isChecked={odcizeni?.id == RiskVariant.OdcizeniYes}
              size="lg"
              onChange={(e) => {
                setAddon({
                  riskId: Risk.ODCPOV,
                  riskVariantId: e.target.checked
                    ? RiskVariant.OdcizeniYes
                    : RiskVariant.OdcizeniNo,
                })
              }}
              isDisabled={isDisabled}
            />
          </FormControl>
        </FormRow>
      </Collapse>
      <FormRow>
        <CompaniesSelection
          allInsurers={allInsurers}
          allInsurersDiscounts={insurersDiscounts ?? []}
          insurersPath={'baseParams.insurers'}
          insurersDiscountsPath="baseParams.insurerDiscounts"
        />

        {allAddonInsurers.length > 0 && (
          <FormControl sx={{ gap: 8 }}>
            <FormLabel>Doplňkové pojišťovny</FormLabel>

            <ChakraRadioGroup
              onChange={(e) => {
                setValue(
                  'baseParams.addonInsurers',
                  e == '1'
                    ? [...(allAddonInsurers?.map((x) => x.id) ?? [])]
                    : [],
                )
              }}
              value={isSelectedAllAddonInsurers ? '1' : '0'}
              isDisabled={isDisabled}
            >
              <Stack
                direction="row"
                spacing={{
                  base: 4,
                  lg: 8,
                }}
              >
                <Radio value="1">Všechny dostupné</Radio>
                <Radio value="0">Vlastní výběr</Radio>
              </Stack>
            </ChakraRadioGroup>

            {!isSelectedAllAddonInsurers && (
              <CompanyCheckboxGroup name="baseParams.addonInsurers">
                {allAddonInsurers.map(({ id, name }) => (
                  <CompanyCheckbox key={id} value={id} logo={getLogo(id)}>
                    <>{name}</>
                  </CompanyCheckbox>
                ))}
              </CompanyCheckboxGroup>
            )}
          </FormControl>
        )}
      </FormRow>
    </>
  )
}
